import { Button } from '@/components/ui/button';
import { TableCell } from '@/components/ui/table';
import { GripVertical, Trash2, FileText, StickyNote } from 'lucide-react';
import { SortableItemRow } from './SortableItemRow';
import { InlineEdit } from './InlineEdit';
import { LinksCell } from './LinksCell';
import { PopoverEdit } from './PopoverEdit';
import { StatusCell } from './StatusCell';
import { ReceiptUploadBtn } from './ReceiptUploadBtn';

/**
 * Renders a single furniture item row inside the room's items table.
 *
 * Pulled out of `RoomDetail` so the parent component stays focused on
 * layout/state plumbing rather than per-cell rendering details.
 */
export const ItemRow = ({
  item,
  readOnly,
  showHiddenLinks,
  onUpdateItem,
  onDeleteItem,
  onHideLink,
  onUnhideLink,
  onChooseLink,
  // Receipts
  showReceipts,
  uploadingReceiptId,
  receipts,
  onUploadReceipt,
  onViewReceipt,
}) => (
  <SortableItemRow key={item.id} id={item.id} readOnly={readOnly}>
    {(handleListeners) => (
      <>
        {!readOnly && (
          <TableCell className="py-1 w-8 p-1">
            <button
              {...handleListeners}
              className="cursor-grab active:cursor-grabbing text-[#5b6773]/40 hover:text-[#2d948c] p-1 touch-none"
              aria-label="Drag to reorder"
              data-testid={`drag-handle-${item.id}`}
              onClick={(e) => e.preventDefault()}
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
          </TableCell>
        )}
        <TableCell className="py-1 min-w-[120px]">
          <InlineEdit
            value={item.name} readOnly={readOnly}
            onChange={v => onUpdateItem(item.id, { name: v })}
            placeholder="Item name"
          />
        </TableCell>
        <TableCell className="py-1 min-w-[120px]">
          <LinksCell
            links={item.links || (item.link ? [item.link] : [])}
            onChange={v => onUpdateItem(item.id, { links: v })}
            readOnly={readOnly}
            hiddenUrls={item.hidden_link_urls || []}
            showHidden={showHiddenLinks}
            onHide={readOnly ? undefined : onHideLink}
            onUnhide={readOnly ? undefined : onUnhideLink}
            onChoose={readOnly ? undefined : onChooseLink}
          />
        </TableCell>
        <TableCell className="py-1 w-10">
          <PopoverEdit
            value={item.specifications} readOnly={readOnly}
            onChange={v => onUpdateItem(item.id, { specifications: v })}
            placeholder="Size, color, dimensions, finish…"
            icon={FileText}
            label="Specifications"
            testId={`specifications-${item.id}`}
          />
        </TableCell>
        <TableCell className="py-1 min-w-[80px]">
          <InlineEdit
            value={item.budget} readOnly={readOnly} isCurrency
            onChange={v => onUpdateItem(item.id, { budget: v })}
            placeholder="$0"
          />
        </TableCell>
        <TableCell className="py-1 min-w-[80px]">
          <InlineEdit
            value={item.listed_price} readOnly={readOnly} isCurrency
            onChange={v => onUpdateItem(item.id, { listed_price: v })}
            placeholder="$0"
          />
        </TableCell>
        <TableCell className="py-1 min-w-[80px]">
          <InlineEdit
            value={item.ordered_price} readOnly={readOnly} isCurrency
            onChange={v => onUpdateItem(item.id, { ordered_price: v })}
            placeholder="$0"
          />
        </TableCell>
        <TableCell className="py-1 min-w-[90px]">
          <StatusCell
            value={item.status}
            onChange={v => onUpdateItem(item.id, { status: v })}
            readOnly={readOnly}
          />
        </TableCell>
        <TableCell className="py-1 w-10">
          <PopoverEdit
            value={item.notes} readOnly={readOnly}
            onChange={v => onUpdateItem(item.id, { notes: v })}
            placeholder="Anything worth remembering about this item…"
            icon={StickyNote}
            label="Notes"
            testId={`notes-${item.id}`}
          />
        </TableCell>
        {showReceipts && (
          <TableCell className="py-1 min-w-[90px]">
            <ReceiptUploadBtn
              itemId={item.id}
              isUploading={uploadingReceiptId === item.id}
              linked={receipts}
              onUpload={(file) => onUploadReceipt(item.id, file)}
              onViewReceipt={(r) => onViewReceipt(r, item.id)}
            />
          </TableCell>
        )}
        {!readOnly && (
          <TableCell className="py-1 w-8">
            <Button
              variant="ghost" size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
              onClick={() => onDeleteItem(item.id)}
              data-testid={`delete-item-${item.id}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </TableCell>
        )}
      </>
    )}
  </SortableItemRow>
);
